// Updated FileViewerModal.js without fileUtils dependency
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faDownload, 
  faExclamationTriangle, 
  faSpinner,
  faFilePdf,
  faFileImage,
  faFileWord,
  faFileExcel,
  faFileVideo,
  faFileAudio,
  faFile
} from '@fortawesome/free-solid-svg-icons';
import styles from './FileViewerModal.module.css';

const FileViewerModal = ({ isOpen, onClose, file }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileContentType, setFileContentType] = useState(null);
  
  // Prepare the file URL when the modal opens
  useEffect(() => {
    if (!isOpen || !file) return;
    
    console.log('=== FileViewerModal PrepareFile DEBUG START ===');
    console.log('File object:', file);
    
    // Set content type
    setFileContentType(file.fileType);
    setError(null);
    
    try {
      // IMPORTANT: Use the exact URL provided in the file object
      // DO NOT attempt to create or modify URLs
      if (!file.fileUrl) {
        throw new Error('No file URL provided');
      }
      
      const displayUrl = file.fileUrl;
      
      console.log('Using provided URL:', displayUrl);
      setFileUrl(displayUrl);
      setIsLoading(false);
      
      console.log('=== FileViewerModal PrepareFile DEBUG END ===');
    } catch (error) {
      console.error('Error preparing file:', error);
      setError(error.message || 'Failed to prepare file');
      setIsLoading(false);
    }
  }, [isOpen, file]);

  // Check if file can be displayed directly without fetching
  const canDisplayDirectly = (fileType) => {
    return fileType?.startsWith('image/') || 
           fileType === 'application/pdf' ||
           canUseGoogleViewer(fileType);
  };

  // Get icon based on file type
  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') {
      return faFilePdf;
    } else if (fileType?.startsWith('image/')) {
      return faFileImage;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
               fileType === 'application/msword') {
      return faFileWord;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
               fileType === 'application/vnd.ms-excel') {
      return faFileExcel;
    } else if (fileType?.startsWith('video/')) {
      return faFileVideo;
    } else if (fileType?.startsWith('audio/')) {
      return faFileAudio;
    } else {
      return faFile;
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // Get friendly file type display name
  const getFileTypeDisplay = (fileType) => {
    const typeMap = {
      'application/pdf': 'PDF Document',
      'application/msword': 'Word Document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
      'application/vnd.ms-excel': 'Excel Spreadsheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
      'text/plain': 'Text Document',
      'application/zip': 'ZIP Archive',
      'application/x-zip-compressed': 'ZIP Archive',
      'image/jpeg': 'JPEG Image',
      'image/png': 'PNG Image',
      'image/gif': 'GIF Image',
      'video/mp4': 'MP4 Video',
      'audio/mpeg': 'MP3 Audio'
    };
    return typeMap[fileType] || fileType;
  };

  // Can we use Google Docs Viewer for this file type?
  const canUseGoogleViewer = (fileType) => {
    const supportedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain'
    ];
    return supportedTypes.includes(fileType);
  };

  // Get Google Docs viewer URL for the file
  const getGoogleViewerUrl = () => {
    return `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
  };

  // Handle download click
  const handleDownload = async () => {
    try {
      // Direct download - let the browser handle it
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      setError(`Failed to download: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FontAwesomeIcon 
              icon={getFileIcon(file.fileType || fileContentType)} 
              className={styles.fileTypeIcon} 
            />
            {file.fileName}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {isLoading ? (
            <div className={styles.loaderContainer}>
              <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
              <p className={styles.loadingText}>Preparing file...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
              <p className={styles.errorMessage}>{error}</p>
              <div className={styles.retryOptions}>
                <button 
                  onClick={() => window.location.reload()}
                  className={styles.retryButton}
                >
                  Retry
                </button>
                <button 
                  onClick={handleDownload}
                  className={styles.downloadButton}
                >
                  Download Instead
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.fileViewer}>
              {(file.fileType?.startsWith('image/') || fileContentType?.startsWith('image/')) ? (
                <div className={styles.imageContainer}>
                  <img
                    src={fileUrl}
                    alt={file.fileName}
                    className={styles.imageViewer}
                    onError={(e) => {
                      console.error('Image failed to load');
                      setError('Failed to load image');
                    }}
                  />
                </div>
              ) : (file.fileType === 'application/pdf' || fileContentType === 'application/pdf') ? (
                <div className={styles.pdfContainer}>
                  <iframe
                    src={fileUrl}
                    title="PDF Viewer"
                    className={styles.pdfViewer}
                    onError={(e) => {
                      console.error('PDF failed to load');
                      // Fallback to Google Docs viewer for PDFs
                      e.target.src = getGoogleViewerUrl();
                    }}
                  />
                </div>
              ) : canUseGoogleViewer(file.fileType || fileContentType) ? (
                // Use Google Docs viewer for Office documents
                <div className={styles.docContainer}>
                  <iframe
                    src={getGoogleViewerUrl()}
                    title="Document Viewer"
                    className={styles.docViewer}
                    frameBorder="0"
                  />
                </div>
              ) : (
                <div className={styles.fileInfoContainer}>
                  <div className={styles.fileInfo}>
                    <FontAwesomeIcon 
                      icon={getFileIcon(file.fileType || fileContentType)} 
                      className={styles.largeFileIcon}
                    />
                    <h3 className={styles.fileName}>{file.fileName}</h3>
                    <p className={styles.fileType}>{getFileTypeDisplay(file.fileType || fileContentType)}</p>
                    <p className={styles.fileSize}>
                      {formatFileSize(file.size)}
                    </p>
                    <p className={styles.downloadMessage}>
                      This file type cannot be previewed directly. 
                      Click the download button below to view it.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className={styles.modalFooter}>
          {!isLoading && (
            <button 
              className={styles.downloadButton}
              onClick={handleDownload}
            >
              <FontAwesomeIcon icon={faDownload} className={styles.downloadIcon} />
              Download
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileViewerModal;